import { Dialog } from "@components/ui/Dialog";
import { AlertTriangle, HelpCircle, Info } from "lucide-react";
import type { ReactNode } from "react";

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: "warning" | "error" | "info" | "primary" | "success";
  isLoading?: boolean;
}

/**
 * Reusable confirmation modal for destructive actions.
 * Wraps the shared Dialog component.
 */
export function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title = "Confirmer l'action",
  message = "Êtes-vous sûr de vouloir continuer ?",
  confirmText = "Confirmer",
  cancelText = "Annuler",
  variant = "warning",
  isLoading = false,
}: ConfirmModalProps) {
  const getIcon = (): ReactNode => {
    switch (variant) {
      case "error":
        return <AlertTriangle className="w-6 h-6 text-error" />;
      case "warning":
        return <AlertTriangle className="w-6 h-6 text-warning" />;
      case "info":
        return <Info className="w-6 h-6 text-info" />;
      default:
        return <HelpCircle className="w-6 h-6 text-base-content/50" />;
    }
  };

  const getConfirmButtonClass = (): string => {
    switch (variant) {
      case "error":
        return "btn-error";
      case "warning":
        return "btn-warning";
      case "info":
        return "btn-info";
      default:
        return "btn-primary";
    }
  };

  const handleConfirm = () => {
    onConfirm();
    // Don't auto-close - let parent handle it after action completes
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      icon={getIcon()}
      dismissable={!isLoading}
      footer={
        <div className="flex gap-2">
          <button onClick={onClose} disabled={isLoading} className="btn btn-ghost btn-sm">
            {cancelText}
          </button>
          <button
            onClick={handleConfirm}
            disabled={isLoading}
            className={`btn btn-sm ${getConfirmButtonClass()} ${isLoading ? "loading" : ""}`}
          >
            {isLoading ? "Chargement..." : confirmText}
          </button>
        </div>
      }
    >
      <p className="text-sm text-base-content/70">{message}</p>
    </Dialog>
  );
}
